import * as signalR from "@microsoft/signalr";
import { PropsWithChildren, useEffect } from "react";
import { useAuthenticated, useAuthProvider } from "react-admin";
import { AuthManager } from "../../../common/auth/Auth";
import {
  LiveTrackTokenApiFactory,
  LiveTrackVehicleDto,
} from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { LiveTrackEvent } from "../types";

interface Props {
  vehicleId: number;
  handleLocationUpdated: (args: LiveTrackEvent) => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
  setVehicle: (vehicle: LiveTrackVehicleDto) => void;
}

export const AuthenticatedLocationLiveTrack = ({
  vehicleId,
  children,
  handleLocationUpdated,
  setError,
  setLoading,
  setVehicle,
}: PropsWithChildren<Props>) => {
  useAuthenticated();
  const authProvider = useAuthProvider() as AuthManager;
  const axios = useAxiosInstance();

  useEffect(() => {
    if (!axios) {
      return;
    }

    LiveTrackTokenApiFactory(undefined, axios?.defaults.baseURL, axios)
      .apiAppLivetracktokenIdVehicleforlivetrackGet(vehicleId)
      .then((response) => {
        if (response.data) {
          setVehicle(response.data);
        }
      })
      .catch((e) =>
        console.error("Unable to get the requested vehicle data", e)
      );
  }, [axios, setVehicle, vehicleId]);

  useEffect(() => {
    const apiUrl =
      process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
      "https://localhost:44327";
    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiUrl}/signalr-hubs/live-track-hub`, {
        accessTokenFactory: () => authProvider.getAccessToken(),
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    hubConnection.on("updateLocation", handleLocationUpdated);
    hubConnection.onreconnected(() => {
      hubConnection
        .send("setVehicleId", vehicleId)
        .catch((e) =>
          console.warn(`Unable to set the vehicle ID to ${vehicleId}`, e)
        );
    });

    hubConnection
      .start()
      .then(() => {
        hubConnection
          .send("setVehicleId", vehicleId)
          .catch((e) =>
            console.warn(`Unable to set the vehicle ID to ${vehicleId}`, e)
          );
      })
      .catch((e) => {
        console.error(e);
        setError(!!e.message ? e.message.toString() : e.toString());
        setLoading(false);
      });

    return () => {
      console.log("Stopping the hub connection");
      hubConnection?.off("updateLocation");
      hubConnection.stop();
    };
  }, [handleLocationUpdated, authProvider, vehicleId, setError, setLoading]);

  return <>{children}</>;
};
